import { CalendarClock, Cloud, HardDrive, Pause, Play, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ActionMenu } from '@/modules/backups/ActionMenu';
import { exactTime, timeUntil } from '@/modules/backups/format';
import { divider, flatPrimary, hairline, toneText } from '@/components/surface/tones';
import { Panel, SectionHeader, StatusPill } from '@/components/surface/ui';
import { relativeTime } from '@/modules/monitoring/format';
import type { BackupDestination, BackupSchedule } from '@/types/api';

/**
 * Automatic backups, one per source.
 *
 * ## A schedule that is producing nothing is flagged
 *
 * `is_stale` means switched on but not delivering — a stopped scheduler or an unreachable server —
 * which otherwise looks identical to one that is working. It is the only red state a schedule has,
 * and it outranks "Active": a schedule that is on and producing nothing is not active in any sense a
 * customer cares about.
 *
 * ## An empty section says what to do, in one line and one button
 *
 * A schedule needs somewhere to put its copies, so until a destination exists the button waits and
 * the line says why, rather than opening a form with nothing to choose in it.
 */
export function SchedulesSection({
  schedules,
  destinations,
  permitsChanges,
  busy,
  onNew,
  onToggle,
  onRemove,
}: {
  schedules: BackupSchedule[];
  destinations: BackupDestination[];
  permitsChanges: boolean;
  busy: { toggle: boolean; remove: boolean };
  onNew: () => void;
  onToggle: (id: string, enabled: boolean) => void;
  onRemove: (id: string) => void;
}) {
  const offsiteById = new Map(destinations.map((d) => [d.id, d.is_offsite]));

  return (
    <Panel id="schedules">
      <SectionHeader
        icon={CalendarClock}
        iconTone="primary"
        title="Automatic backups"
        description="Old copies are removed once a newer one exists."
        actions={
          schedules.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={onNew} disabled={!permitsChanges} className="text-muted-foreground hover:text-foreground">
              <Plus />
              New schedule
            </Button>
          ) : undefined
        }
      />

      {schedules.length === 0 ? (
        <div className="px-5 pb-5">
          <p className="text-[13px] text-muted-foreground">
            Nothing is backed up automatically yet.{' '}
            {destinations.length === 0
              ? 'Add backup storage first, then choose a source and how many copies to keep.'
              : 'Choose a source, where its copies go and how many to keep.'}
          </p>
          <Button
            size="sm"
            onClick={onNew}
            disabled={!permitsChanges || destinations.length === 0}
            className={cn('mt-3', flatPrimary)}
          >
            <Plus />
            New schedule
          </Button>
        </div>
      ) : (
        <ul className={cn('divide-y border-t', hairline, divider)}>
          {schedules.map((schedule) => {
            const state = schedule.is_stale
              ? ({ tone: 'danger', label: 'Not running' } as const)
              : schedule.enabled
                ? ({ tone: 'success', label: 'Active' } as const)
                : ({ tone: 'muted', label: 'Paused' } as const);
            const offsite = offsiteById.get(schedule.destination_id);

            return (
              <li key={schedule.id} className="px-5 py-3">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 truncate text-sm font-medium">{schedule.source_name}</span>
                  <StatusPill tone={state.tone} label={state.label} />
                  <div className="ml-auto flex shrink-0 items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToggle(schedule.id, !schedule.enabled)}
                      disabled={busy.toggle}
                      className="h-7 px-2 text-muted-foreground hover:text-foreground"
                    >
                      {schedule.enabled ? <Pause /> : <Play />}
                      {schedule.enabled ? 'Pause' : 'Resume'}
                    </Button>
                    <ActionMenu
                      label={`More actions for the ${schedule.source_name} schedule`}
                      items={[
                        {
                          label: 'Remove schedule',
                          icon: Trash2,
                          hint: 'Backups already taken are kept.',
                          tone: 'danger',
                          disabled: busy.remove,
                          onSelect: () => onRemove(schedule.id),
                        },
                      ]}
                    />
                  </div>
                </div>

                <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                  <span>{schedule.frequency_label}</span>
                  <span aria-hidden>·</span>
                  <span>{schedule.scope_label}</span>
                  <span aria-hidden>·</span>
                  <span>
                    keep {schedule.retention_count} {schedule.retention_count === 1 ? 'copy' : 'copies'}
                  </span>
                  <span aria-hidden>·</span>
                  <span className="inline-flex min-w-0 items-center gap-1">
                    {offsite === true && <Cloud className="size-3 shrink-0" aria-hidden />}
                    {offsite === false && <HardDrive className="size-3 shrink-0" aria-hidden />}
                    <span className="truncate">{schedule.destination_name}</span>
                  </span>
                </p>

                <p className="mt-0.5 text-xs text-muted-foreground">
                  {schedule.last_success_at ? (
                    <span title={exactTime(schedule.last_success_at)} className={schedule.is_stale ? toneText.danger : undefined}>
                      Last succeeded {relativeTime(schedule.last_success_at)}
                    </span>
                  ) : (
                    <span className={schedule.enabled ? toneText.warning : undefined}>Has never succeeded</span>
                  )}
                  {schedule.enabled && (
                    <span title={exactTime(schedule.next_due_at)}> · next {timeUntil(schedule.next_due_at)}</span>
                  )}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
